import { LoadingOutlined } from '@ant-design/icons';
import { Spin } from 'antd';
import {
  Dispatch,
  RefObject,
  SetStateAction,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useInView } from 'react-intersection-observer';
import styled from 'styled-components';

import FoldedHandsSvg from '../../../assets/icons/FoldedHandsSvg';
import useDebounce from '../../../hooks/useDebounce';
import useHandleRoundAddedFromDiscover from '../../../hooks/useHandleRoundAddedFromDiscover';
import { useMetrics } from '../../../hooks/useMetrics';
import useTemplateSearch from '../../../hooks/useTemplateSearch';
import {
  DiscoverSearchQuery,
  IndexedTemplate,
} from '../../../services/backendService/types';
import { shuffleArray } from '../../../utils/arrayUtils';
import { IconWrapper } from '../../_atoms/IconWrapper';
import SearchInput from '../../_molecules/SearchInput';
import { Slide } from '../../SlideComponents/slide.types';
import { findRelatedSlidesWithoutIndex } from '../../SlideComponents/slideUtil';
import { makeNewIdsForSlide } from '../../SlideComponents/slideUtilCopyPaste';
import { CenteredContainer } from '../../styled/CenteredContainer';
import DesignerLessonCard from '../DesignerLessonCard';
import CoreAddSlidesExperience from './CoreAddSlidesExperience';

const getRandomTemplateSearchValue = () => {
  return shuffleArray([
    'Animal habitat',
    'Solar system',
    'water cycle',
    'Main idea review',
    'equations',
    'Ai in the classroom',
    'poetry',
    'persuasive writing',
    'plants',
    'civil war',
    'American revolution',
    'persuasive writing',
    'climate change',
  ])[0];
};
const TemplateSearchExperience = ({
  onTemplateSelected,
  searchValue,
  setSearchValue,
  wrapperRef,
  setScrollPosition,
}: {
  onTemplateSelected: (template: IndexedTemplate) => void;
  searchValue: string | undefined;
  wrapperRef: RefObject<HTMLDivElement>;
  setSearchValue: Dispatch<SetStateAction<string | undefined>>;
  setScrollPosition: Dispatch<SetStateAction<number>>;
}) => {
  const [hoveredTemplate, setHoveredTemplate] = useState<string | undefined>();
  const searchQuery = useMemo<DiscoverSearchQuery>(
    () => ({
      limit: 25,
      filters: undefined,
      searchPhrase: searchValue ? searchValue : getRandomTemplateSearchValue(),
    }),
    [searchValue],
  );

  const debouncedSearchQuery = useDebounce(searchQuery, 500);

  const { ref: scrollRef, inView } = useInView();

  const {
    items: allResults,
    canFetchMore,
    isLoading,
    fetchMore,
    isFetchingMore,
  } = useTemplateSearch(debouncedSearchQuery);

  useEffect(() => {
    if (inView && canFetchMore && !isFetchingMore) {
      fetchMore();
    }
  }, [inView, canFetchMore, fetchMore, isFetchingMore]);

  return (
    <TabContainer>
      <FixedSearchFilterContainer style={{ marginBottom: 6 }}>
        <SearchInput
          placeholder={`Search ${`for "${searchQuery.searchPhrase}"` || 'all lessons'}`}
          value={searchValue}
          style={{ width: '100%' }}
          onChange={setSearchValue}
        />
      </FixedSearchFilterContainer>
      <ResultsContainer ref={wrapperRef} id="scrollable">
        {allResults?.pages.flatMap(res =>
          res.items.map(item => (
            <ScrollResponsiveLessonCardContainer
              key={item.id}
              onMouseEnter={() => setHoveredTemplate(item.id)}
              onMouseLeave={() => setHoveredTemplate(undefined)}
            >
              <DesignerLessonCard
                isHovering={hoveredTemplate === item.id}
                template={item}
                setSearchValue={setSearchValue}
                onClick={() => {
                  onTemplateSelected(item);
                  if (wrapperRef.current) {
                    setScrollPosition(wrapperRef.current?.scrollTop || 0);
                    setSearchValue(searchQuery.searchPhrase || '');
                  }
                }}
              />
            </ScrollResponsiveLessonCardContainer>
          )),
        )}
        {isLoading && !allResults && (
          <CenteredContainer>
            <Spin indicator={<LoadingOutlined style={{ fontSize: 24 }} spin />} />
          </CenteredContainer>
        )}
        {isFetchingMore && (
          <CenteredContainer style={{ marginTop: 40 }}>
            <Spin indicator={<LoadingOutlined style={{ fontSize: 24 }} spin />} />
          </CenteredContainer>
        )}
        {allResults?.pages[0].total === 0 && !isLoading && (
          <CenteredContainer style={{ flexDirection: 'column' }}>
            <IconWrapper height="100px" width="100px">
              <FoldedHandsSvg />
            </IconWrapper>
            <p
              style={{
                fontWeight: 'bold',
                width: '100%',
                padding: '0px 20px',
                textAlign: 'center',
              }}
            >
              Could not find results, please try another search
            </p>
          </CenteredContainer>
        )}
        <div ref={scrollRef}></div>
      </ResultsContainer>
    </TabContainer>
  );
};

const ScrollResponsiveLessonCardContainer = styled.div`
  margin-bottom: 20px;
`;

const ResultsContainer = styled.div`
  height: calc(100% - 48px);
  overflow-y: auto;
  padding: 20px;
`;

//TODO: rename rounds to slides
const TemplatesTab = ({
  onAddActivity,
  setSearchTemplatesValue,
  searchTemplatesValue,
  templateId,
}: {
  onAddActivity: (slides: Slide[]) => void;
  searchTemplatesValue?: string;
  setSearchTemplatesValue: Dispatch<SetStateAction<string | undefined>>;
  templateId: string;
}) => {
  const wrapperRef = useRef<HTMLDivElement>(null);
  const { handleSlideAddedFromDiscover } = useHandleRoundAddedFromDiscover();
  const [selectedTemplate, setSelectedTemplate] = useState<IndexedTemplate>();
  const [scrollPosition, setScrollPosition] = useState<number>(0);
  const metrics = useMetrics();
  const handleAddSlides = (slides: Slide[]) => {
    const newSlides = makeNewIdsForSlide(
      slides.flatMap(round => {
        const related = findRelatedSlidesWithoutIndex(
          round.id,
          selectedTemplate?.slides || [],
        );
        return related;
      }),
    );

    onAddActivity(newSlides);
    // This is being called to notify the backend that it should copy files as rounds have
    // been added directly from discover and may include custom uploaded images and media
    handleSlideAddedFromDiscover({ slides: newSlides });
    metrics.logEvent('TemplatesTab.AddRoundsToLesson', {
      originTemplateId: selectedTemplate?.id,
      destinationTemplateId: templateId,
      count: newSlides.length,
    });
  };

  const scrollEffect = ({
    parentElement,
    scrollTop,
  }: {
    parentElement: RefObject<HTMLDivElement>;
    scrollTop: number;
  }) => {
    parentElement.current?.scrollTo({
      top: scrollTop,
    });
  };

  useLayoutEffect(() => {
    if (wrapperRef.current && selectedTemplate === undefined) {
      scrollEffect({ parentElement: wrapperRef, scrollTop: scrollPosition });
      setScrollPosition(0);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedTemplate]);

  if (selectedTemplate) {
    return (
      <CoreAddSlidesExperience
        rounds={selectedTemplate.slides}
        title={selectedTemplate.title}
        onGoBack={() => setSelectedTemplate(undefined)}
        onAddRounds={handleAddSlides}
        isDiscoverTemplate={true}
        isOwner={false}
        style={{ paddingTop: 20 }}
        metricsContext={{
          ...selectedTemplate,
          tab: 'TemplatesTab',
        }}
      />
    );
  }

  return (
    <TemplateSearchExperience
      onTemplateSelected={setSelectedTemplate}
      wrapperRef={wrapperRef}
      setScrollPosition={setScrollPosition}
      setSearchValue={setSearchTemplatesValue}
      searchValue={searchTemplatesValue}
    />
  );
};

const TabContainer = styled.div`
  padding-top: 20px;
  height: 100%;
`;

const FixedSearchFilterContainer = styled.div`
  padding-left: 20px;
  padding-right: 20px;
`;

export default TemplatesTab;
